import { setAttributes } from '../../global/render-api.js';
import { UIElement } from '../ui-element.js';
import styles from './ui-curtains.css';

/**
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UICurtains
 * @element ui-curtains
 * @classdesc Represents a class for <code>ui-curtains</code> element.
 * Container for ui-curtain elements.
 * @listens event:curtain-toggle
 * @property {boolean} [autoclose=false] {@attr autoclose} If true, curtain will
 * close automatically, when other curtain inside ui-curtains is opened. Acts like accordion.
 * Works only for numbered curtains.
 * @property {boolean} [autoscroll=false] {@attr autoscroll} Enables for each curtain in ui-curtains - scroll
 * in smaller viewport. When user opens a curtain, window scrolls to it's offset.
 * @property {UICurtainsLayouts} [layout] {@attr layout} Layout of curtains.
 *  {@desc default}
 *  {@desc numbered}
 * @property {number} [startIndex=1] {@attr start-index} Start index for numbered curtains
 * @property {Array<UICurtain>} curtains {@readonly} Shortcut to array of curtains inside wrapper ui-curtains.
 * @slot {@type "ui-curtain"}
 * @example
 * <ui-curtains layout="numbered">
 *   <ui-curtain label="Label for 1st">Content 1</ui-curtain>
 *   <ui-curtain label="Label for 2nd">Content 2</ui-curtain>
 *   <ui-curtain label="Label for 3rd">Content 3</ui-curtain>
 * </ui-curtains>
 */
class UICurtains extends UIElement {
    /**
     * @type {IProps}
     * @readonly
     */
    static get props() {
        return {
            attributes: {
                autoclose: Boolean,
                layout: String,
                autoscroll: Boolean,
                startIndex: { type: Number, default: 1 },
            },
            children: {
                curtains: {
                    selector: ':scope > ui-curtain',
                    multiple: true,
                },
            },
        };
    }

    /**
     * @type {Props}
     */
    static get observedAttributes() {
        return ['autoclose'];
    }

    /**
     * Layout type of curtains.
     * @type {UICurtainsLayouts}
     */
    static get Layout() {
        return {
            Default: 'default',
            Numbered: 'numbered',
        };
    }

    /**
     * Is curtain inside this wrapper - numbered/completed
     * @returns {boolean}
     */
    isNumbered() {
        return (
            this.layout === UICurtains.Layout.Numbered ||
            !!this.querySelector(
                ':scope > ui-curtain[index], :scope > ui-curtain[completed]'
            )
        );
    }

    /**
     * Util to set curtains inside ui-curtains with numbers, starting from startIndex
     */
    initNumberedCurtains() {
        const length = this.curtains.length;
        this.curtains.forEach((curtain, index) => {
            curtain.setAttribute('index', this.startIndex + index);
            setAttributes(curtain, {
                'aria-posinset': index + 1,
                'aria-setsize': length,
            });
        });
    }

    /**
     * Set all curtains inside ui-curtains auto-scrolled after opening
     */
    initAutoscroll() {
        this.curtains.forEach((curtain) => {
            curtain.setAttribute('autoscroll', '');
        });
    }

    /**
     * Close each curtain except first if 'autoclose' functionality is switched on.
     */
    prepareAutocloseCurtains() {
        [].forEach.call(
            this.queryChildren('ui-curtain[open] ~ ui-curtain[open]'),
            (element) => (element.open = false)
        );
        this.addEventListener('curtain-toggle', this.handleCurtainToggle);
    }

    /**
     * Fires callback when curtain-toggle event will bubble.
     * @param {UIEvent} e
     * @private
     */
    handleCurtainToggle(e) {
        if (this !== e.target.closest('ui-curtains')) {
            return;
        }

        [].forEach.call(this.queryChildren('ui-curtain[open]'), (target) => {
            if (target !== e.target) {
                target.open = false;
            }
        });
    }

    /**
     * @inheritDoc
     */
    observeAttributes(name, oldValue, newValue) {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }
        switch (name) {
            case 'autoclose':
                if (newValue === null || !this.isNumbered()) {
                    this.removeEventListener(
                        'curtain-toggle',
                        this.handleCurtainToggle
                    );
                    return;
                }
                this.prepareAutocloseCurtains();
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    render() {
        if (this.autoscroll) {
            this.initAutoscroll();
        }
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        if (this.layout === UICurtains.Layout.Numbered) {
            this.initNumberedCurtains();
        }
        this.handleCurtainToggle = this.handleCurtainToggle.bind(this);
        if (this.autoclose && this.isNumbered()) {
            this.addEventListener('curtain-toggle', this.handleCurtainToggle);
        }
    }
}

UICurtains.defineElement('ui-curtains', styles);
export { UICurtains };
